import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Grid from '@material-ui/core/Grid'
import { createSignal } from '@react-rxjs/utils'
import classnames from 'classnames'
import React, { useCallback, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { SanityHeroTextOptionDocument } from '../../../../../../../groq/sanity-dispensaries/types'
import { DispensaryHeroFormValues } from '../CustomizeHomePage'
import { DisabledOverlay } from '../DisabledOverlay'
import * as Styled from './styled'

export interface HeroTextSelectProps {
  disabled: boolean
  options: ReadonlyArray<SanityHeroTextOptionDocument>
  optionForSlug: SanityHeroTextOptionDocument | null
  renderEntireImageAsCta: DispensaryHeroFormValues['renderEntireImageAsCta']
}

const [checkTextOptionAfterReset$, checkTextOptionAfterReset] = createSignal<void>()
export { checkTextOptionAfterReset }

const HeroTextSelect: React.FC<HeroTextSelectProps> = ({
  disabled,
  options,
  optionForSlug,
  renderEntireImageAsCta,
}) => {
  const { control, getValues, register, setValue } = useFormContext<DispensaryHeroFormValues>()

  useEffect(() => {
    const subscription = checkTextOptionAfterReset$.subscribe(() => {
      const firstOptionId = options[0]?._id
      const selectedId = getValues('selectedTextOptionId')
      if (!selectedId && firstOptionId) {
        setValue('selectedTextOptionId', firstOptionId, { shouldDirty: true })
      }
    })
    return () => subscription.unsubscribe()
  }, [options, getValues, setValue])

  const { selectedTextOptionId } = useWatch<DispensaryHeroFormValues>({
    control,
    name: ['selectedTextOptionId'],
  })
  const handleSelectionChange = useCallback(
    (ref: string) => {
      setValue('selectedTextOptionId', ref, { shouldDirty: true })
    },
    [setValue],
  )

  return (
    <Styled.StyledGrid container spacing={2}>
      {options.map((option) => (
        <Grid key={option._id} item xs={12}>
          <Styled.Button
            disabled={disabled}
            onClick={() => handleSelectionChange(option._id)}
            className={classnames({
              selected: selectedTextOptionId === option._id && renderEntireImageAsCta === 'no',
            })}
          >
            <Styled.ParagraphText>{option.text}</Styled.ParagraphText>
            <Styled.ButtonText>{option.ctaText}</Styled.ButtonText>
          </Styled.Button>
          <Styled.IconContainer
            onClick={() => handleSelectionChange(option._id)}
            className={classnames({
              selected: selectedTextOptionId === option._id && renderEntireImageAsCta === 'no',
            })}
          >
            <FontAwesomeIcon icon={faCheckCircle} />
          </Styled.IconContainer>
        </Grid>
      ))}
      <Grid item xs={12}>
        <Styled.Button
          disabled={disabled}
          onClick={() => handleSelectionChange(optionForSlug?._id || 'new')}
          className={classnames({
            selected:
              selectedTextOptionId === (optionForSlug?._id || 'new') &&
              renderEntireImageAsCta === 'no',
          })}
        >
          Create Your Own
        </Styled.Button>
        <Styled.LastIconContainer
          onClick={() => handleSelectionChange(optionForSlug?._id || 'new')}
          className={classnames({
            selected:
              selectedTextOptionId === (optionForSlug?._id || 'new') &&
              renderEntireImageAsCta === 'no',
          })}
        >
          <FontAwesomeIcon icon={faCheckCircle} />
        </Styled.LastIconContainer>
      </Grid>
      <input type="hidden" name="selectedTextOptionId" ref={register} />
      {renderEntireImageAsCta === 'yes' && (
        <DisabledOverlay>
          Banner Text fields are disabled while Render Entire Image as CTA is enabled.
        </DisabledOverlay>
      )}
    </Styled.StyledGrid>
  )
}

export default HeroTextSelect
